import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../Component/Sidebar';
import axiosInstance from '../api/axiosInstance'; // Make sure this path is correct

// Import enhanced thunks
import {
  fetchEmployeeThunk,
  fetchEnhancedEmployeeIncentiveSummaryThunk,
  fetchDetailedIncentiveHistoryThunk
} from '../features/employees/employeeThunks';
import { fetchReportsThunk } from '../features/reports/reportThunks';

// Role rank constants (matching backend and Sidebar)
const ROLE_RANKS = {
  'ADMIN': 5,
  'TECH': 5,
  'TEAM_LEADER': 4,
  'HR': 4,
  'SALES': 4,
  'QUALITY': 3,
  'SPA': 2,
  'PA': 1,
};

// Helper function to check if user is admin
const isUserAdmin = (user) => {
  if (!user || !user.employee_profile) return false;
  const role = user.employee_profile.role;
  return ROLE_RANKS[role] === 5 && role === 'ADMIN';
};

const EmployeePage = () => {
  const { employeeId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthlyReportCount, setMonthlyReportCount] = useState(0);

  // Department name mapping
  const departmentNames = {
    'TECH_BACKEND': 'Tech Team (Backend)',
    'DEVELOPMENT': 'Development Team',
    'HR': 'HR',
    'OPERATIONS': 'Operations',
    'SALES_MARKETING': 'Sales/Marketing',
    'FLOOR_SUPPORT': 'Floor Support'
  };
  // UPDATED: isBreakdownVisible is now false by default to hide it initially
  const [isBreakdownVisible, setIsBreakdownVisible] = useState(false); 
  const [isPastBreakdownVisible, setIsPastBreakdownVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = 20;

  // NEW: State for report date filtering
  const [reportFilterDates, setReportFilterDates] = useState({ start_date: '', end_date: '' });
  const [appliedReportFilterDates, setAppliedReportFilterDates] = useState({ start_date: null, end_date: null });


  // Redux selectors
  const { currentEmployee, loading: employeeLoading, enhancedIncentiveSummary, detailedIncentiveHistory } = useSelector(state => state.employees);
  const { reports: reportsList, loading: reportsLoading, error: reportsError, count: totalReportCount } = useSelector(state => state.reports);
  const { user } = useSelector(state => state.auth);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  // Permission checking
  const isOwnProfile = user?.employee_profile?.id?.toString() === employeeId?.toString();
  const isAdmin = user?.employee_profile?.role === 'ADMIN';
  const canViewIncentives = isOwnProfile || isAdmin;
  
  // Check if current user is admin (for Update Profile button visibility)
  const currentUserIsAdmin = isUserAdmin(user);

  // Generate year options for dropdown
  const yearOptions = Array.from({ length: 3 }, (_, i) => currentYear - i);
  const monthOptions = [
    { value: null, label: 'Select Month' },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: new Date(0, i).toLocaleString('default', { month: 'long' })
    }))
  ];

  // Data fetching logic
  useEffect(() => {
    const fetchMonthlyReportCount = async () => {
      const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).toISOString().split('T')[0];
      const lastDayOfMonth = new Date(currentYear, currentMonth, 0).toISOString().split('T')[0];
      const params = new URLSearchParams({ 
        limit: 1, 
        employee_id: employeeId,
        start_date: firstDayOfMonth,
        end_date: lastDayOfMonth
      });
      try {
        const { data } = await axiosInstance.get(`/api/reports/?${params}`);
        setMonthlyReportCount(data.count || 0);
      } catch (error) {
        console.error("Failed to fetch this month's report count:", error);
      }
    };
    
    if (employeeId) {
      const offset = (currentPage - 1) * reportsPerPage;
      dispatch(fetchEmployeeThunk(employeeId));
      // UPDATED: fetchReportsThunk now includes date filters
      dispatch(fetchReportsThunk({ 
        employee_id: employeeId,
        limit: reportsPerPage,
        offset: offset,
        start_date: appliedReportFilterDates.start_date,
        end_date: appliedReportFilterDates.end_date,
      })); 
      fetchMonthlyReportCount();
      if (canViewIncentives) {
        dispatch(fetchEnhancedEmployeeIncentiveSummaryThunk({
          employeeId,
          year: selectedYear,
          month: selectedMonth
        }));
        dispatch(fetchDetailedIncentiveHistoryThunk({
          employeeId,
          year: selectedYear,
          month: selectedMonth,
          limit: 20
        }));
      }
    }
  }, [dispatch, employeeId, canViewIncentives, selectedYear, selectedMonth, currentYear, currentMonth, currentPage, appliedReportFilterDates]);

  // NEW: Handlers for report date filter
  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setReportFilterDates(prev => ({ ...prev, [name]: value }));
  };

  const applyDateFilter = () => {
    setAppliedReportFilterDates(reportFilterDates);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const clearDateFilter = () => {
    setReportFilterDates({ start_date: '', end_date: '' });
    setAppliedReportFilterDates({ start_date: null, end_date: null });
    setCurrentPage(1); // Reset to page 1
  };

  // Handle year/month filter change for past incentives
  const handleFilterChange = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
    setIsPastBreakdownVisible(false);
  };

  // Load more past incentive transactions
  const loadMoreTransactions = () => {
    if (detailedIncentiveHistory.next && !detailedIncentiveHistory.loading) {
      const offset = detailedIncentiveHistory.list.length;
      dispatch(fetchDetailedIncentiveHistoryThunk({
        employeeId,
        year: selectedYear,
        month: selectedMonth,
        limit: 20,
        offset
      }));
    }
  };

  // Navigate to the employee edit page
  const handleUpdate = () => {
    navigate(`/employees/${employeeId}/edit`);
  };

  // Find the summary data for the month selected in the sidebar filter
  const selectedMonthData = enhancedIncentiveSummary.data?.monthly_breakdown?.find(
    month => month.month === selectedMonth
  );

  // Helper functions for formatting
  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount || 0);
  const getStatusBadge = (status) => {
    const config = {
      'APPROVED': { class: 'employee-page-status-completed', text: 'Approved' },
      'SUBMITTED_REVIEW': { class: 'employee-page-status-review', text: 'In Review' },
      'SUBMITTED_APPROVAL': { class: 'employee-page-status-review', text: 'Pending Approval' },
      'DECLINED': { class: 'employee-page-status-declined', text: 'Declined' },
      'DISCREPANCY': { class: 'employee-page-status-discrepancy', text: 'Discrepancy' },
      'PENDING': { class: 'employee-page-status-review', text: 'Pending' },
      'PAID': { class: 'employee-page-status-completed', text: 'Paid' },
      'CANCELLED': { class: 'employee-page-status-declined', text: 'Cancelled' }
    }[status] || { class: 'employee-page-status-review', text: status };
    return `<span class="employee-page-status-badge ${config.class}">${config.text}</span>`;
  };

  // Loading and error states
  if (employeeLoading && !currentEmployee) {
    return (
        <div className='index-box'><Sidebar /><div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><h2>Loading Employee Profile...</h2></div></div>
    );
  }
  if (!currentEmployee) {
    return (
        <div className='index-box'><Sidebar /><div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><h2>Employee Not Found</h2></div></div>
    );
  }

  // Calculate total pages for pagination based on the count from the API
  const totalPages = Math.ceil(totalReportCount / reportsPerPage);
  
  // NEW: Condition to show main skeleton loader
  const showSkeleton = employeeLoading || (reportsLoading && reportsList.length === 0);

  return (
    <>
      <style>{`
        /* Enhanced Employee Page Styles */
        .employee-page-container { min-height: 100vh; background-color: transparent; display: flex; justify-content: center; align-items: flex-start; width:80%; }
        .employee-page-main-card { width: 100%; max-width: 1400px; background-color: white; border-radius: 0.75rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); overflow: hidden; }
        .employee-page-header-section { position: relative; }
        .employee-page-gradient-background { height: 12rem; background: linear-gradient(to right, #bbf7d0, #fde047, #fb923c); }
        .employee-page-profile-image-container { position: absolute; bottom: 0; left: 2rem; transform: translateY(50%); }
        .employee-page-profile-image-wrapper { width: 6rem; height: 6rem; border-radius: 50%; border: 4px solid white; overflow: hidden; background: #f3f4f6; display: flex; align-items: center; justify-content: center; }
        .employee-page-profile-image { width: 100%; height: 100%; object-fit: cover; object-position: top; }
        .employee-page-profile-placeholder { font-size: 1.5rem; font-weight: bold; color: #6b7280; }
        .employee-page-action-buttons { position: absolute; bottom: 1rem; right: 2rem; display: flex; align-items: center; gap: 1rem; }
        .employee-page-contact-button { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 1rem; border-radius: 0.5rem; cursor: pointer; border: none; transition: background-color 0.2s; background-color: #10b981; color: white; font-weight: 500; }
        .employee-page-contact-button:hover { background-color: #059669; }
        .employee-page-profile-info { padding: 4rem 2rem 2rem; }
        .employee-page-content-wrapper { display: grid; grid-template-columns: 1fr 400px; gap: 2rem; }
        .employee-page-main-content { flex: 1; }
        .employee-page-employee-name { font-size: 1.875rem; font-weight: 700; margin: 0; }
        .employee-page-job-title { color: #6b7280; margin: 0.5rem 0; }
        .employee-page-company-info { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #9ca3af; margin-bottom: 2rem; }
        .employee-page-filters { display: flex; gap: 1rem; margin-bottom: 1.5rem; padding: 1rem; background: #f8fafc; border-radius: 0.5rem; }
        .employee-page-filter-group { display: flex; flex-direction: column; gap: 0.5rem; flex: 1; }
        .employee-page-filter-group label { font-size: 0.875rem; font-weight: 500; color: #374151; }
        .employee-page-filter-group select { padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 6px; background: white; }
        .employee-page-metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; margin-bottom: 2.5rem; }
        .employee-page-metric-card { padding: 1.5rem; border-radius: 0.75rem; text-align: center; color: white; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1); }
        .employee-page-metric-card.primary { background: linear-gradient(135deg, #2563eb, #1d4ed8); }
        .employee-page-metric-card.secondary { background: linear-gradient(135deg, #16a34a, #15803d); }
        .employee-page-metric-card.tertiary { background: linear-gradient(135deg, #ea580c, #c2410c); }
        .employee-page-metric-value { font-size: 2.25rem; font-weight: 700; margin-bottom: 0.5rem; }
        .employee-page-metric-label { font-size: 0.875rem; opacity: 0.9; }
        .employee-page-section-title { font-size: 1.25rem; font-weight: 600; margin-bottom: 1.5rem; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.75rem; }
        .employee-page-reports-section, .employee-page-monthly-breakdown { margin-bottom: 2.5rem; }
        .employee-page-table-container { overflow-x: auto; }
        .employee-page-reports-table { width: 100%; border-collapse: collapse; }
        .employee-page-table-header th { background-color: #f9fafb; padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb;}
        .employee-page-table-row { border-bottom: 1px solid #e5e7eb; transition: background-color 0.2s; }
        .employee-page-table-row:hover { background-color: #f3f4f6; cursor: pointer; }
        .employee-page-table-row:last-child { border-bottom: none; }
        td { padding: 0.75rem 1rem; vertical-align: middle; }
        .employee-page-store-cell { display: flex; align-items: center; gap: 0.75rem; }
        .employee-page-store-image { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background-color: #e5e7eb; color: #4b5563; display: flex; justify-content: center; align-items: center; font-weight: 600; font-size: 0.875rem; flex-shrink: 0; }
        .employee-page-status-badge { padding: 0.25rem 0.75rem; display: inline-flex; font-size: 0.75rem; font-weight: 600; border-radius: 9999px; }
        .employee-page-status-completed { background-color: #dcfce7; color: #166534; }
        .employee-page-status-review { background-color: #fef3c7; color: #92400e; }
        .employee-page-status-declined, .employee-page-status-discrepancy { background-color: #fee2e2; color: #991b1b; }
        .employee-page-month-card { border: 1px solid #e5e7eb; border-radius: 0.5rem; margin-bottom: 1rem; overflow: hidden; }
        .employee-page-month-header { background: #f9fafb; padding: 1rem; font-weight: 600; display: flex; justify-content: space-between; align-items: center; cursor: pointer; }
        .employee-page-month-content { padding: 1rem; border-top: 1px solid #e5e7eb; }
        .employee-page-store-breakdown { display: grid; gap: 0.5rem; margin-top: 1rem; }
        .employee-page-store-item { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; background: white; border: 1px solid #e5e7eb; border-radius: 6px; }
        .employee-page-store-name { font-weight: 500; }
        .employee-page-store-metrics { display: flex; gap: 1rem; font-size: 0.875rem; color: #6b7280; }
        .employee-page-toggle-button { background-color: #e5e7eb; border: none; padding: 0.25rem 0.75rem; border-radius: 99px; font-size: 0.75rem; font-weight: 500; cursor: pointer; }
        .employee-page-toggle-button:hover { background-color: #d1d5db; }
        .employee-page-sidebar { width: 100%; }
        .employee-page-sidebar-card { background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 0.75rem; padding: 1.5rem; margin-bottom: 1.5rem; }
        .employee-page-incentive-summary { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; background: #eef2ff; border: 1px solid #c7d2fe; border-radius: .5rem; font-weight: 600; margin-top: 1rem; }
        .employee-page-incentive-amount { color: #4338ca; }
        .employee-page-transaction-list { display: flex; flex-direction: column; gap: 0.75rem; max-height: 400px; overflow-y: auto; padding-right: 0.5rem; }
        .employee-page-transaction-item { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; border: 1px solid #e5e7eb; border-radius: 6px; background: white; }
        .employee-page-transaction-info { flex: 1; }
        .employee-page-transaction-store { font-weight: 500; font-size: 0.875rem; }
        .employee-page-transaction-date { font-size: 0.75rem; color: #6b7280; }
        .employee-page-transaction-amount { font-weight: 600; color: #10b981; margin-left: 1rem; }
        .employee-page-contact-info { display: flex; flex-direction: column; gap: 1.5rem; }
        .employee-page-contact-item { display: flex; align-items: flex-start; gap: 1rem; }
        .employee-page-contact-icon { width: 1.25rem; color: #9ca3af; font-size: 1.25rem; text-align: center; }
        .employee-page-contact-details p { margin: 0; }
        .employee-page-contact-primary { color: #1f2937; font-weight: 500; }
        .employee-page-contact-secondary { font-size: 0.875rem; color: #6b7280; }
        .employee-page-empty-state, .employee-page-no-incentives { text-align: center; padding: 2rem; color: #6b7280; font-style: italic; background-color: #f9fafb; border-radius: .5rem; }
        .employee-page-load-more { width: 100%; padding: 0.75rem; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px; color: #374151; cursor: pointer; transition: all 0.2s; margin-top: 1rem; }
        .employee-page-load-more:hover { background: #e5e7eb; }
        .employee-page-load-more:disabled { opacity: 0.5; cursor: not-allowed; }
        .employee-page-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #e5e7eb; }
        .employee-page-pagination button { background-color: white; border: 1px solid #d1d5db; padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer; }
        .employee-page-pagination button:disabled { background-color: #f9fafb; color: #9ca3af; cursor: not-allowed; }
        .employee-page-pagination span { font-size: 0.875rem; font-weight: 500; color: #6b7280; }
        @media (max-width: 1024px) { .employee-page-content-wrapper { grid-template-columns: 1fr; } }
        @media (max-width: 768px) { .employee-page-filters { flex-direction: column; } .employee-page-metrics-grid { grid-template-columns: 1fr; } .employee-page-profile-info { padding: 4rem 1rem 1rem; } }

        /* NEW: Skeleton Loading Styles */
        @keyframes skeleton-loading {
            0% { background-color: #e5e7eb; }
            100% { background-color: #f3f4f6; }
        }
        .skeleton {
            animation: skeleton-loading 1.2s linear infinite alternate;
            border-radius: 0.375rem;
            background-color: #e5e7eb;
        }
        .skeleton-text { height: 1rem; width: 80%; }
        .skeleton-title { height: 1.5rem; width: 50%; margin-bottom: 1rem; }
        .skeleton-avatar { width: 6rem; height: 6rem; border-radius: 50%; }
        .skeleton-button { height: 2.25rem; width: 120px; }

        /* NEW: Report Filter Styles */
        .employee-page-section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 1rem;
            margin-bottom: 1.5rem;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 0.75rem;
        }
        .employee-page-report-filters {
            display: flex;
            gap: 0.75rem;
            align-items: center;
        }
        .employee-page-report-filters input {
            padding: 0.375rem 0.75rem;
            border: 1px solid #d1d5db;
            border-radius: 0.375rem;
            font-size: 0.875rem;
        }
        .employee-page-report-filters button {
            padding: 0.375rem 1rem;
            border-radius: 0.375rem;
            border: none;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s;
        }
        .employee-page-filter-btn { background-color: #3b82f6; color: white; }
        .employee-page-filter-btn:hover { background-color: #2563eb; }
        .employee-page-clear-btn { background-color: #e5e7eb; color: #374151; }
        .employee-page-clear-btn:hover { background-color: #d1d5db; }
        .employee-page-reports-info {
          font-size: 0.875rem; color: #6b7280;
        }

      `}</style>

      <div className='index-box' style={{ display: 'flex' }}>
        <Sidebar />
        <div className="employee-page-container">
            <div className="employee-page-main-card">
            <div className="employee-page-header-section">
                <div className="employee-page-gradient-background"></div>
                <div className="employee-page-profile-image-container">
                <div className="employee-page-profile-image-wrapper">
                    {currentEmployee.image ? ( <img src={currentEmployee.image} alt="Profile" className="employee-page-profile-image" /> ) : ( <div className="employee-page-profile-placeholder">{currentEmployee.user?.first_name?.[0]}{currentEmployee.user?.last_name?.[0]}</div> )}
                </div>
                </div>
                {/* Only show Update Profile button to admins */}
                {currentUserIsAdmin && (
                  <div className="employee-page-action-buttons">
                    <button onClick={handleUpdate} className="employee-page-contact-button">
                      Update Profile
                    </button>
                  </div>
                )}
            </div>

            <div className="employee-page-profile-info">
                <div className="employee-page-content-wrapper">
                
                <div className="employee-page-main-content">
                    <h1 className="employee-page-employee-name">{currentEmployee.user?.first_name} {currentEmployee.user?.last_name}</h1>
                    <p className="employee-page-job-title">{currentEmployee.role || 'Employee'}</p>
                    {currentEmployee.department && (
                      <p className="employee-page-job-title" style={{fontSize: '14px', color: '#666', marginTop: '4px'}}>
                        🏢 {departmentNames[currentEmployee.department] || currentEmployee.department}
                      </p>
                    )}
                    <div className="employee-page-company-info"><i className="fas fa-envelope"></i><span>{currentEmployee.user?.email}</span></div>

                    <div className="employee-page-metrics-grid">
                        {showSkeleton ? (
                          <>
                            <div className="employee-page-metric-card skeleton"></div>
                            <div className="employee-page-metric-card skeleton"></div>
                            <div className="employee-page-metric-card skeleton"></div>
                          </>
                        ) : (
                          <>
                            <div className="employee-page-metric-card primary"><div className="employee-page-metric-value">{totalReportCount ?? '...'}</div><div className="employee-page-metric-label">Total Reports Created</div></div>
                            <div className="employee-page-metric-card secondary"><div className="employee-page-metric-value">{monthlyReportCount}</div><div className="employee-page-metric-label">Reports This Month</div></div>
                            {canViewIncentives && (<div className="employee-page-metric-card tertiary"><div className="employee-page-metric-value">{formatCurrency(enhancedIncentiveSummary.data?.current_month?.total_amount)}</div><div className="employee-page-metric-label">Incentive This Month</div></div>)}
                          </>
                        )}
                    </div>

                    {canViewIncentives && enhancedIncentiveSummary.data?.current_month?.store_breakdown?.length > 0 && (
                        <div className="employee-page-monthly-breakdown">
                            <h2 className="employee-page-section-title">This Month's Incentive Breakdown</h2>
                            <div className="employee-page-month-card">
                                <div className="employee-page-month-header" onClick={() => setIsBreakdownVisible(!isBreakdownVisible)}>
                                    <span>{enhancedIncentiveSummary.data.current_month.month_name} {currentYear}</span>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                                        <span>{formatCurrency(enhancedIncentiveSummary.data.current_month.total_amount)}</span>
                                        <div className="employee-page-toggle-button">{isBreakdownVisible ? 'Hide' : 'Show'}</div>
                                    </div>
                                </div>
                                {isBreakdownVisible && ( <div className="employee-page-month-content"><div className="employee-page-store-breakdown" style={{marginTop: 0}}>{enhancedIncentiveSummary.data.current_month.store_breakdown.map(store => (<div key={store.store__id} className="employee-page-store-item"><div className="employee-page-store-name">{store.store__store_name}</div><div className="employee-page-store-metrics"><span>{formatCurrency(store.store_amount)}</span><span>•</span><span>{store.store_reports} reports</span></div></div>))}</div></div> )}
                            </div>
                        </div>
                    )}
                    
                    <div className="employee-page-reports-section">
                        {/* NEW: Section Header with Date Filters */}
                        <div className="employee-page-section-header">
                            <h2 className="employee-page-section-title" style={{border: 'none', margin: 0, padding: 0}}>Recent Reports</h2>
                            <div className="employee-page-report-filters">
                                <input type="date" name="start_date" value={reportFilterDates.start_date} onChange={handleDateFilterChange} />
                                <span>to</span>
                                <input type="date" name="end_date" value={reportFilterDates.end_date} onChange={handleDateFilterChange} />
                                <button onClick={applyDateFilter} className="employee-page-filter-btn">Filter</button>
                                <button onClick={clearDateFilter} className="employee-page-clear-btn">Clear</button>
                            </div>
                        </div>

                        <div className="employee-page-table-container">
                            {/* UPDATED: Added skeleton loading for the table */}
                            {reportsLoading && reportsList.length === 0 ? (
                                <table className="employee-page-reports-table">
                                    <thead className="employee-page-table-header"><tr><th>Incident Type</th><th>Date & Time</th><th>Store</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {[...Array(5)].map((_, i) => (
                                            <tr key={i}>
                                                <td><div className="skeleton skeleton-text" style={{width: '90%'}}></div></td>
                                                <td><div className="skeleton skeleton-text" style={{width: '70%'}}></div></td>
                                                <td><div className="skeleton skeleton-text" style={{width: '80%'}}></div></td>
                                                <td><div className="skeleton skeleton-text" style={{width: '50%'}}></div></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : reportsList.length > 0 ? (
                            <table className="employee-page-reports-table">
                                <thead className="employee-page-table-header"><tr><th>Incident Type</th><th>Date & Time</th><th>Store</th><th>Status</th></tr></thead>
                                <tbody className="employee-page-table-body">
                                {reportsList.map(report => (
                                    <tr key={report.id} className="employee-page-table-row" onClick={() => navigate(`/reports/${report.id}/edit`)}>
                                    <td>{report.incident_type || 'N/A'}</td>
                                    <td>
                                        {new Date(report.incident_date).toLocaleString('en-US', {
                                            year: 'numeric', month: 'short', day: 'numeric',
                                            hour: 'numeric', minute: '2-digit', hour12: true
                                        })}
                                    </td>
                                    <td>
                                        <div className="employee-page-store-cell">
                                            {report.store?.image ? (
                                                <img src={report.store.image} alt={report.store.store_name} className="employee-page-store-image" />
                                            ) : (
                                                <div className="employee-page-store-image">
                                                {report.store?.store_name?.[0] || 'S'}
                                                </div>
                                            )}
                                            <span>{report.store?.store_name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td><div dangerouslySetInnerHTML={{ __html: getStatusBadge(report.status) }} /></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            ) : (<div className="employee-page-empty-state"><p>No recent reports found for the selected period.</p></div>)}
                        </div>
                        {totalPages > 1 && (
                            <div className="employee-page-pagination">
                                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>Previous</button>
                                {/* NEW: Total reports count display */}
                                <div className='employee-page-reports-info'>
                                  <span>Page {currentPage} of {totalPages} </span>
                                  <span style={{marginLeft: '1rem'}}> (Total: <strong>{totalReportCount}</strong> reports)</span>
                                </div>
                                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>Next</button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="employee-page-sidebar">
                    {/* UPDATED: Added skeleton loading for sidebar */}
                    {showSkeleton ? (
                      <>
                        <div className="employee-page-sidebar-card skeleton" style={{height: '300px', marginBottom: '1.5rem'}}></div>
                        <div className="employee-page-sidebar-card skeleton" style={{height: '200px'}}></div>
                      </>
                    ) : (
                      <>
                       <div className="employee-page-sidebar-card">
                            <h2 className="employee-page-section-title">Details</h2>
                            <div className="employee-page-contact-info">
                                <div className="employee-page-contact-item"><div className="employee-page-contact-icon"><i className="fas fa-user-tie"></i></div><div className="employee-page-contact-details"><p className="employee-page-contact-primary">{currentEmployee.role || 'Employee'}</p><p className="employee-page-contact-secondary">Role</p></div></div>
                                {currentEmployee.department && ( <div className="employee-page-contact-item"><div className="employee-page-contact-icon"><i className="fas fa-building"></i></div><div className="employee-page-contact-details"><p className="employee-page-contact-primary">{departmentNames[currentEmployee.department] || currentEmployee.department}</p><p className="employee-page-contact-secondary">Department</p></div></div> )}
                                {currentEmployee.contact_no && ( <div className="employee-page-contact-item"><div className="employee-page-contact-icon"><i className="fas fa-phone"></i></div><div className="employee-page-contact-details"><p className="employee-page-contact-primary">{currentEmployee.contact_no}</p><p className="employee-page-contact-secondary">Phone</p></div></div> )}
                                {currentEmployee.date_of_joining && ( <div className="employee-page-contact-item"><div className="employee-page-contact-icon"><i className="fas fa-calendar-alt"></i></div><div className="employee-page-contact-details"><p className="employee-page-contact-primary">{new Date(currentEmployee.date_of_joining).toLocaleDateString()}</p><p className="employee-page-contact-secondary">Joining Date</p></div></div> )}
                            </div>
                        </div>
                        {canViewIncentives && (
                            <div className="employee-page-sidebar-card">
                                <h2 className="employee-page-section-title">Past Incentives</h2>
                                <div className="employee-page-filters" style={{flexDirection: 'column', alignItems: 'stretch', background: 'transparent', padding: '0 0 1rem 0'}}>
                                <div className="employee-page-filter-group"><label>Year</label><select value={selectedYear} onChange={(e) => handleFilterChange(parseInt(e.target.value), selectedMonth)}>{yearOptions.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                                <div className="employee-page-filter-group"><label>Month</label><select value={selectedMonth || ''} onChange={(e) => handleFilterChange(selectedYear, e.target.value ? parseInt(e.target.value, 10) : null)}>{monthOptions.map(m => <option key={m.value || 'all'} value={m.value || ''}>{m.label}</option>)}</select></div>
                                </div>
                                
                                {selectedMonth && selectedMonthData ? (
                                    <div>
                                        <div className="employee-page-incentive-summary"><span>Total for {selectedMonthData.month_name}:</span><span className="employee-page-incentive-amount">{formatCurrency(selectedMonthData.total_amount)}</span></div>
                                        {selectedMonthData.store_breakdown?.length > 0 && (<button className="employee-page-load-more" onClick={() => setIsPastBreakdownVisible(!isPastBreakdownVisible)}>{isPastBreakdownVisible ? 'Hide Breakdown' : 'Show Breakdown'}</button>)}
                                        {isPastBreakdownVisible && (<div className="employee-page-store-breakdown">{selectedMonthData.store_breakdown.map(store => (<div key={store.store__id} className="employee-page-store-item"><div className="employee-page-store-name">{store.store__store_name}</div><div className="employee-page-store-metrics"><span>{formatCurrency(store.store_amount)}</span></div></div>))}</div>)}
                                    </div>
                                ) : (
                                    <>
                                    <div className="employee-page-transaction-list">
                                    {detailedIncentiveHistory.list?.length > 0 ? ( detailedIncentiveHistory.list.map(t => (<div key={t.id} className="employee-page-transaction-item"><div className="employee-page-transaction-info"><div className="employee-page-transaction-store">{t.store_name}</div><div className="employee-page-transaction-date">{new Date(t.created_at).toLocaleDateString()}</div></div><div className="employee-page-transaction-amount">{formatCurrency(t.amount)}</div></div>)) ) : ( <div className="employee-page-no-incentives">No transactions for this period.</div> )}
                                    </div>
                                    {detailedIncentiveHistory.next && ( <button className="employee-page-load-more" onClick={loadMoreTransactions} disabled={detailedIncentiveHistory.loading}>{detailedIncentiveHistory.loading ? 'Loading...' : 'Load More'}</button> )}
                                    </>
                                )}
                            </div>
                        )}
                        
                       
                      </>
                    )}
                </div>
                </div>
            </div>
            </div>
        </div>
      </div>
    </>
  );
};

export default EmployeePage;